import { reactive } from 'vue';
import useImages from '~/composables/images/useImages';
import { useDeviceType } from '~/composables/utils/useDeviceType';

const SCROLL_SPEED_MULTIPLIER = 16;
const MIN_SWIPE_DISTANCE = 5;
const MOVEMENT_THRESHOLD = 7;
/**
 *
 * @param {Object} object
 * @param {import('vue').Ref<number>} object.currentPage
 * @param {import('vue').Ref<number>} object.prevIndex
 * @param {any} object.flags
 *
 * @param {{
 *   childWidth: number,
 *   wrapWidth: number | null,
 *   itemsLength: number
 * }} object.dimensions
 *
 * @param {import('vue').Ref<number>} object.scrollerX
 * @param {import('vue').Ref<number | null>} object.scrollerWidth
 * @param {import('vue').Ref<boolean>} object.hasBeenDragged
 *
 * @param {{
 *  start: () => void
 *  stop: () =>
 * }} object.autoplay
 * @param {'auto' | 'manual' }  object.autoplayMode,
 * @param {{
 *  move: (flag: number) => boolean
 *  change: (index: number) => void
 *  scrollerPosition: (page: number) => number
 * }} object.navigation
 *
 * @param {(eventName: 'slideshow-change' | 'load' | 'update-first-alt-dot', ...args: any[]) => void} object.emit
 */

const useSlideshowTouch = ({
  currentPage,
  prevIndex,
  flags,
  scrollerX,
  hasBeenDragged,
  scrollerWidth,
  dimensions,
  autoplay,
  autoplayMode,
  navigation,
  emit,
  isZoomedIn,
}) => {
  const { isMobile } = useDeviceType();
  const { carouselImages } = useImages();

  const touch = reactive({
    dragging: false,
    isScrollingOnMobile: false,
    isSwipingOnMobile: false,
    startX: 0,
    touchstartX: 0,
    touchstartY: 0,
    touchmoveX: 0,
    touchmoveY: 0,
    touchendX: 0,
    touchendY: 0,
    velocity: 0,
    lastMoveTime: 0,
    lastMoveX: 0,
    animationId: null,
  });

  const resetSwipeAndScrollAction = () => {
    touch.isScrollingOnMobile = false;
    touch.isSwipingOnMobile = false;
  };

  const calculateMostVisibleImage = () => {
    if (!isMobile.value || !dimensions.wrapWidth) {
      return currentPage.value; // fallback to current
    }

    const scrollPosition = Math.abs(scrollerX.value);
    const viewportWidth = dimensions.wrapWidth;
    let maxVisibleArea = 0;
    let mostVisibleIndex = 0;

    carouselImages.value.forEach((item, index) => {
      const itemLeft = index * dimensions.childWidth;
      const itemRight = itemLeft + dimensions.childWidth;

      // Calculate visible area of this item
      const visibleLeft = Math.max(itemLeft, scrollPosition);
      const visibleRight = Math.min(itemRight, scrollPosition + viewportWidth);
      const visibleArea = Math.max(0, visibleRight - visibleLeft);

      if (visibleArea > maxVisibleArea) {
        maxVisibleArea = visibleArea;
        mostVisibleIndex = index;
      }
    });

    return mostVisibleIndex + 1;
  };

  const updateCurrentPageFromViewport = () => {
    if (!isMobile.value) return;

    const newPage = calculateMostVisibleImage();
    if (newPage !== currentPage.value) {
      currentPage.value = newPage;
      emit('slideshow-change', currentPage.value);
    }
  };

  const getScreenX = (event, isMouse) =>
    isMouse ? /** @type {MouseEvent} */ (event).screenX : /** @type {TouchEvent} */ (event).changedTouches[0].screenX;
  const getScreenY = (event, isMouse) =>
    isMouse ? /** @type {MouseEvent} */ (event).screenY : /** @type {TouchEvent} */ (event).changedTouches[0].screenY;

  const startDragging = (event, isMouse) => {
    touch.dragging = true;
    touch.startX = scrollerX.value;
    touch.touchstartX = getScreenX(event, isMouse);
    touch.touchstartY = getScreenY(event, isMouse);

    if (isMobile.value) {
      touch.velocity = 0;
      touch.lastMoveTime = Date.now();
      touch.lastMoveX = touch.touchstartX;
    }
  };

  /**
   * @param {MouseEvent | TouchEvent} event
   * @param {boolean} isMouse
   */
  const scrollerTouchStart = (event, isMouse) => {
    if (autoplayMode === 'auto') {
      autoplay.stop();
    }
    if (flags.topLevelZoomerMobile.value && isZoomedIn.value) {
      return;
    }
    resetSwipeAndScrollAction();

    if (carouselImages.value.length < 2) return;

    startDragging(event, isMouse);
  };

  const updateSwipeState = (distanceX, distanceY) => {
    if (!touch.isScrollingOnMobile && !touch.isSwipingOnMobile) {
      if (Math.abs(distanceY) > Math.abs(distanceX)) {
        touch.isScrollingOnMobile = true;
      } else if (Math.abs(distanceX) > MIN_SWIPE_DISTANCE) {
        touch.isSwipingOnMobile = true;
      }
    }
  };

  const handleSwipeMove = (distanceX) => {
    const maxScroll = dimensions.wrapWidth && scrollerWidth.value ? Math.min(0, -(scrollerWidth.value - dimensions.wrapWidth)) : 0;
    let nextX = touch.startX - distanceX;

    if (nextX > 0) {
      nextX = 0;
    } else if (nextX < maxScroll) {
      nextX = maxScroll;
    }

    if (isMobile.value) {
      const now = Date.now();
      const timeDiff = now - touch.lastMoveTime;
      if (timeDiff > 0) {
        touch.velocity = (touch.touchmoveX - touch.lastMoveX) / timeDiff;
        touch.lastMoveTime = now;
        touch.lastMoveX = touch.touchmoveX;
        scrollerX.value = nextX;
      }
    } else {
      scrollerX.value = nextX;

      if (touch.startX - distanceX > 0 || touch.startX - distanceX < maxScroll) return;

      prevIndex.value = currentPage.value + (distanceX > 0 ? 1 : -1);
    }
  };

  /**
   * @param {MouseEvent | TouchEvent} event
   * @param {boolean} isMouse
   */
  const scrollerTouchMove = (event, isMouse) => {
    if (flags.topLevelZoomerMobile.value && isZoomedIn.value) {
      return;
    }
    if (carouselImages.value.length < 2 || !touch.dragging || isZoomedIn.value) {
      return;
    }

    touch.touchmoveX = getScreenX(event, isMouse);
    touch.touchmoveY = getScreenY(event, isMouse);
    const distanceX = touch.touchstartX - touch.touchmoveX;
    const distanceY = touch.touchstartY - touch.touchmoveY;

    updateSwipeState(distanceX, distanceY);

    if (touch.isScrollingOnMobile) {
      return;
    }

    if (touch.isSwipingOnMobile) {
      if (!isMouse) {
        event.preventDefault();
      }

      hasBeenDragged.value = true;
      handleSwipeMove(distanceX);
    }
  };

  /**
   * @param {number} initialVelocity
   *
   */
  const startContinuousMovement = (initialVelocity) => {
    let currentVelocity = initialVelocity;
    const friction = 0.95;
    const minVelocity = 0.01;

    const animate = () => {
      scrollerX.value += currentVelocity * SCROLL_SPEED_MULTIPLIER;

      const maxScroll = -(scrollerWidth.value - dimensions.wrapWidth);
      if (scrollerX.value > 0) {
        scrollerX.value = 0;
        currentVelocity = 0;
      } else if (scrollerX.value < maxScroll) {
        scrollerX.value = maxScroll;
        currentVelocity = 0;
      }

      currentVelocity *= friction;

      if (Math.abs(currentVelocity) > minVelocity) {
        touch.animationId = requestAnimationFrame(animate);
      } else {
        touch.animationId = null;
      }
      updateCurrentPageFromViewport();
    };
    touch.animationId = requestAnimationFrame(animate);
  };

  /**
   * @param {number} swipeDistance
   */
  const handleSwipe = (swipeDistance) => {
    if (!isMobile.value) {
      if (swipeDistance > 0) {
        navigation.move(1);
      } else if (swipeDistance < 0) {
        navigation.move(-1);
      }
    } else {
      const threshold = 1 - flags.thresholdLimit;
      const distance = Math.abs(touch.touchstartX - touch.touchendX);
      const hasPassedThreshold = distance > dimensions.childWidth * threshold;
      if (hasPassedThreshold) {
        if (touch.touchendX < touch.touchstartX) {
          if (!navigation.move(1)) {
            scrollerX.value = touch.startX;
          }
        } else if (touch.touchendX > touch.touchstartX) {
          if (!navigation.move(-1)) {
            scrollerX.value = touch.startX;
          }
        }
      } else {
        scrollerX.value = touch.startX;
        prevIndex.value = -1;
      }
    }
  };

  /**
   * @param {MouseEvent | TouchEvent} event
   * @param {boolean} isMouse
   */
  const scrollerTouchEnd = (event, isMouse) => {
    if (!touch.dragging || isZoomedIn.value) {
      return;
    }

    touch.touchendX = getScreenX(event, isMouse);
    touch.touchendY = getScreenY(event, isMouse);

    const distanceX = touch.touchstartX - touch.touchendX;
    const distanceY = touch.touchstartY - touch.touchendY;

    if (Math.abs(distanceY) && touch.isScrollingOnMobile) {
      return;
    }

    if (isMobile.value) {
      if (Math.abs(distanceX) > MOVEMENT_THRESHOLD) {
        startContinuousMovement(touch.velocity);
        touch.dragging = false;
        return;
      }
      hasBeenDragged.value = false;
      touch.dragging = false;
      return;
    }

    if (Math.abs(distanceX) > MOVEMENT_THRESHOLD) {
      handleSwipe(distanceX);
    }
    resetSwipeAndScrollAction();
    touch.dragging = false;
    if (!isMouse) {
      hasBeenDragged.value = false;
    }
  };

  /**
   * @param {Event} event
   */
  const preventClickAfterDrag = (event) => {
    if (hasBeenDragged.value) {
      event.stopImmediatePropagation();
      event.preventDefault();
      hasBeenDragged.value = false;
    }
  };

  /**
   * @param {MouseEvent | TouchEvent} event
   */
  const documentMouseUp = (event) => scrollerTouchEnd(event, true);

  /**
   * @param {MouseEvent | TouchEvent} event
   */
  const documentMouseMove = (event) => scrollerTouchMove(event, true);

  return {
    touch,
    scrollerTouchStart,
    scrollerTouchMove,
    scrollerTouchEnd,
    preventClickAfterDrag,
    documentMouseUp,
    documentMouseMove,
  };
};

export default useSlideshowTouch;
